/**
 * Font sizes are unitless numbers meaning "px on web, dp on RN" so both platforms scale
 * identically; web's theme layer is responsible for turning them into `rem` strings and RN's
 * theme layer consumes them directly. `lineHeight` is a unitless ratio (CSS accepts unitless
 * line-height directly; RN's `lineHeight` prop is absolute, so the RN theme layer must compute
 * `fontSize * lineHeight`). `letterSpacing` is in `em` (web multiplies into the CSS `em` unit;
 * RN multiplies by `fontSize` to get an absolute dp value).
 *
 * ── Design System V2 · Type ─────────────────────────────────────────────────────────────────
 * A cleaner, more confident hierarchy: larger, tighter display sizes for page titles; heavier,
 * more deliberate heading weights; roomier line-heights on body copy for readability. Inter
 * remains the family — its wide range of weights carries the premium feel without a new font load.
 */
export const fontFamily = {
  base: 'Inter',
  /** Optional display family for hero titles — falls back to base everywhere it isn't loaded. */
  display: 'Inter',
  fallbackWeb:
    '"Inter", "Inter var", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
} as const;

export const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/**
 * The literal MUI-variant-shaped scale (h1–h6/body1/body2/caption/button/overline) — this is what
 * `willnq-web`'s theme adapter maps 1:1 onto MUI's `typography` theme key.
 */
export const typeScale = {
  h1: { fontSize: 44, lineHeight: 1.1, weight: fontWeights.bold, letterSpacing: -0.02 },
  h2: { fontSize: 36, lineHeight: 1.15, weight: fontWeights.bold, letterSpacing: -0.018 },
  h3: { fontSize: 28, lineHeight: 1.25, weight: fontWeights.bold, letterSpacing: -0.014 },
  h4: { fontSize: 22, lineHeight: 1.3, weight: fontWeights.semibold, letterSpacing: -0.01 },
  h5: { fontSize: 18, lineHeight: 1.4, weight: fontWeights.semibold, letterSpacing: -0.006 },
  h6: { fontSize: 16, lineHeight: 1.45, weight: fontWeights.semibold, letterSpacing: 0 },
  body1: { fontSize: 16, lineHeight: 1.6, weight: fontWeights.regular, letterSpacing: 0 },
  body2: { fontSize: 14, lineHeight: 1.55, weight: fontWeights.regular, letterSpacing: 0 },
  caption: { fontSize: 12, lineHeight: 1.5, weight: fontWeights.medium, letterSpacing: 0.01 },
  button: { fontSize: 14, lineHeight: 1.2, weight: fontWeights.semibold, letterSpacing: 0.01 },
  overline: { fontSize: 11, lineHeight: 1.4, weight: fontWeights.semibold, letterSpacing: 0.08 },
} as const;

export type TypeScaleKey = keyof typeof typeScale;

/**
 * Semantic roles — the vocabulary component specs (`components/*.ts`) and the future RN Paper
 * theme should reference (RN Paper's own MD3 scale is `displayLarge`/`headlineLarge`/
 * `titleLarge`/`bodyLarge`/`labelLarge`; these roles are the same idea, one tier each).
 */
export const typeRoles = {
  display: typeScale.h1,
  heading: typeScale.h2,
  title: typeScale.h3,
  subtitle: typeScale.h5,
  body: typeScale.body1,
  caption: typeScale.caption,
  button: typeScale.button,
  overline: typeScale.overline,
} as const;

export type TypeRoleKey = keyof typeof typeRoles;
